import * as React from 'react';
import * as autoBind from 'react-autobind';
import * as classNames from 'classnames';
import Constants from '../../globals/Constants';
import LocalStorageWrapper from '../../services/LocalStorageWrapper/LocalStorageWrapper';
import TextAreaWrapper from '../TextAreaWrapper/TextAreaWrapper';
import {ControlButton, TscButtonType, Glyphs} from '@techsmith/tsc-cloud-style-guide';
import Strings from '../../globals/Strings';
import Themeable from '../../types/Themeable';

export default class EditBox extends React.Component<EditBoxProps, EditBoxState> {
   readonly state: EditBoxState = {editText: LocalStorageWrapper.getItem(LocalStorageWrapper.getKey(Constants.localStorageKeys.editComment, this.props.commentId)) || this.props.originalText};

   constructor(props: EditBoxProps) {
      super(props);
      autoBind(this);
   }

   removeUnsavedEdit(): void {
      LocalStorageWrapper.removeItem(LocalStorageWrapper.getKey(Constants.localStorageKeys.editComment, this.props.commentId));
      this.props.setUnsavedEdit(null);
   }

   cancel(): void {
      this.removeUnsavedEdit();
      this.props.cancel();
   }

   delete(): void {
      this.removeUnsavedEdit();
      this.props.deleteText();
   }

   save(): void {
      if (this.state.editText.length > Constants.comment.maxLength) {
         return;
      }

      if (this.state.editText !== this.props.originalText || this.props.deleteAnnotationOnSave) {
         this.props.save(this.state.editText);
      } else {
         this.props.cancel();
      }

      this.removeUnsavedEdit();
   }

   handleChange(text: string): void {
      this.setState({editText: text});
      LocalStorageWrapper.setItem(LocalStorageWrapper.getKey(Constants.localStorageKeys.editComment, this.props.commentId), text);
      this.props.setUnsavedEdit(text);
   }

   render() {
      const {editText} = this.state;
      const {saving, originalText, themeName, readOnly, showEditedLabel, commentHasAnnotation} = this.props;

      const textLimitDifference = editText.length - Constants.comment.maxLength;
      const textLimitMessage = textLimitDifference === 1 ? ' character past limit' : ' characters past limit';
      const disableSave = saving || textLimitDifference > 0;
      const textInputClasses = classNames('text-edit-input', {'past-limit': textLimitDifference > 0});
      const isOriginalAndEditTextEmpty = !originalText && !editText && !commentHasAnnotation;
      const disableDelete = saving || isOriginalAndEditTextEmpty;

      return (
         <div className="edit-box">
            {!readOnly && <TextAreaWrapper text={editText} autoFocus={true} className={textInputClasses} onChange={this.handleChange} onEscape={this.cancel} onEnter={this.save}/>}
            {readOnly && <pre className="edit-box-readonly">{editText}</pre>}
            {showEditedLabel && <div className="edit-label">{Strings.labels.edited}</div>}

            {textLimitDifference > 0 && <div className="text-limit">{textLimitDifference + textLimitMessage}</div>}
            <div className="button-group">
               <ControlButton
                  themeName={themeName}
                  title={Strings.labels.delete}
                  buttonType={TscButtonType.secondary}
                  glyph={<Glyphs.Delete16x16/>}
                  onClick={this.delete}
                  disabled={disableDelete}
                  testId="edit-box-delete-button"
                  />
               <ControlButton
                  themeName={themeName}
                  buttonType={TscButtonType.secondary}
                  isFullWidth={true}
                  onClick={this.cancel}
                  disabled={saving}
                  label={Strings.labels.cancel}
                  testId="edit-box-cancel-button"
                  />
               <ControlButton
                  themeName={themeName}
                  buttonType={TscButtonType.primary}
                  isFullWidth={true}
                  disabled={disableSave}
                  onClick={this.save}
                  label={Strings.labels.save}
                  testId="edit-box-save-button"
                  />
            </div>
         </div>
      );
   }
}

export interface EditBoxProps extends Themeable {
   originalText: string;
   saving: boolean;
   commentId: string;
   deleteAnnotationOnSave: boolean;
   readOnly: boolean;
   showEditedLabel: boolean;
   commentHasAnnotation: boolean;
   cancel(): void;
   save(text: string): Promise<void>;
   deleteText(): void;
   setUnsavedEdit(text: string): void;
}

export interface EditBoxState {
   editText: string;
}
